import type { AxiosInstance } from "axios";
import { ArrowLeft, Check, Gift, Loader2, PauseCircle, PlayCircle, Store as StoreIcon, Users, X } from "lucide-react";
import { useCallback, useEffect, useState } from "react";

import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card.js";
import { Select } from "../components/ui/select.js";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table.js";
import { apiErrorMessage } from "../lib/api.js";
import {
  assignPlan,
  fetchDirectory,
  fetchEntitlements,
  fetchOrgMembers,
  fetchPlans,
  isComped,
  setOrgStatus,
  setStoreStatus,
  type AdminMember,
  type DirectoryOrg,
  type EntitlementInspection,
  type PlanSummary,
  type SubscriptionStatus,
} from "../lib/admin.js";
import { StatusBadge } from "./status-badge.js";

/**
 * One organization: what it can see today, and the controls to change that.
 *
 * THE COMP BUTTON IS THE POINT. "Give the platform free to an org" is not a special flag —
 * it is assigning a plan with a MANUAL provider and an ACTIVE status. Manual means "settled
 * outside any provider", so no Razorpay/Shopify charge is ever created and no webhook can
 * move the subscription; it stays active until an admin changes it. Because assignPlan on
 * the server always writes provider=manual, "Comp" and "Assign plan" are the same call —
 * Comp just preselects an active status and is labelled so it is never done by accident.
 */
export function OrgDetailScreen({
  client,
  orgId,
  onBack,
}: {
  readonly client: AxiosInstance;
  readonly orgId: string;
  readonly onBack: () => void;
}): React.JSX.Element {
  const [org, setOrg] = useState<DirectoryOrg | null>(null);
  const [ent, setEnt] = useState<EntitlementInspection | null>(null);
  const [plans, setPlans] = useState<readonly PlanSummary[] | null>(null);
  const [members, setMembers] = useState<readonly AdminMember[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [dir, entitlements, planList, memberList] = await Promise.all([
        fetchDirectory(client),
        fetchEntitlements(client, orgId),
        fetchPlans(client),
        fetchOrgMembers(client, orgId),
      ]);
      setOrg(dir.find((o) => o.id === orgId) ?? null);
      setEnt(entitlements);
      setPlans(planList);
      setMembers(memberList);
      setError(null);
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not load this organization."));
    }
  }, [client, orgId]);

  useEffect(() => {
    void load();
  }, [load]);

  return (
    <div className="flex w-full flex-col gap-4">
      <button
        type="button"
        onClick={onBack}
        className="text-muted-foreground hover:text-foreground flex w-fit items-center gap-1.5 text-sm"
      >
        <ArrowLeft className="size-4" /> All organizations
      </button>

      {error !== null && <p className="text-destructive text-sm">{error}</p>}

      {ent === null || org === null || plans === null ? (
        <p className="text-muted-foreground flex items-center gap-2 p-6 text-sm">
          <Loader2 className="size-4 animate-spin" /> Loading…
        </p>
      ) : (
        <>
          <header className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-semibold">{ent.orgName}</h1>
                <Badge variant="outline" className="capitalize">
                  {org.kind}
                </Badge>
                {isComped(org.subscription) && <Badge className="border-transparent bg-emerald-600 text-white">Comp</Badge>}
                {org.status === "paused" && <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">Paused</Badge>}
              </div>
              <p className="text-muted-foreground text-sm">
                {org.billingEmail} · {org.storeCount} stores · {org.memberCount} members
              </p>
            </div>
            <OrgStatusButton client={client} orgId={orgId} paused={org.status === "paused"} onChanged={() => void load()} />
          </header>

          <PlanCard org={org} ent={ent} plans={plans} client={client} orgId={orgId} onChanged={() => void load()} />

          <Card>
            <CardHeader>
              <CardTitle className="text-base">What this org can see</CardTitle>
              <CardDescription>
                {ent.planCode === null
                  ? "No plan assigned — no reports are included."
                  : ent.trialExpired
                    ? "Trial expired — access is paused until a plan is assigned."
                    : "Reports included on the current plan."}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <ul className="grid gap-1.5 sm:grid-cols-2">
                {ent.reports.map((r) => (
                  <li key={r.key} className="flex items-center gap-2 text-sm">
                    {r.included ? (
                      <Check className="size-4 text-emerald-600 dark:text-emerald-400" />
                    ) : (
                      <X className="text-muted-foreground/50 size-4" />
                    )}
                    <span className={r.included ? "" : "text-muted-foreground"}>{r.key}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-base">
                <Users className="size-4" /> People
              </CardTitle>
              <CardDescription>
                {members === null ? "Loading…" : `${String(members.length)} member${members.length === 1 ? "" : "s"} in this organization.`}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead>Workspace</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {(members ?? []).map((m) => (
                      <TableRow key={m.userId}>
                        <TableCell className="font-medium">{m.name}</TableCell>
                        <TableCell className="text-muted-foreground">{m.email}</TableCell>
                        <TableCell className="capitalize">{m.role}</TableCell>
                        <TableCell className="text-muted-foreground">{m.clientName ?? "Org-wide"}</TableCell>
                        <TableCell>
                          {m.status === "invited" ? (
                            <Badge variant="secondary">Invited</Badge>
                          ) : (
                            <Badge variant="outline">Active</Badge>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          <StoresCard org={org} client={client} onChanged={() => void load()} />
        </>
      )}
    </div>
  );
}

function OrgStatusButton({
  client,
  orgId,
  paused,
  onChanged,
}: {
  readonly client: AxiosInstance;
  readonly orgId: string;
  readonly paused: boolean;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  async function toggle(): Promise<void> {
    setBusy(true);
    try {
      await setOrgStatus(client, orgId, paused ? "active" : "paused");
      onChanged();
    } catch (caught) {
      void caught;
      onChanged();
    } finally {
      setBusy(false);
    }
  }
  return (
    <Button
      type="button"
      variant={paused ? "default" : "outline"}
      onClick={() => void toggle()}
      disabled={busy}
      className={paused ? "bg-emerald-600 text-white hover:bg-emerald-500" : ""}
      title={paused ? "Resume all data flow for this org" : "Suspend all data flow for this org"}
    >
      {busy ? <Loader2 className="size-4 animate-spin" /> : paused ? <PlayCircle className="size-4" /> : <PauseCircle className="size-4" />}
      {paused ? "Resume org" : "Pause org"}
    </Button>
  );
}

function StoresCard({
  org,
  client,
  onChanged,
}: {
  readonly org: DirectoryOrg;
  readonly client: AxiosInstance;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const stores = org.clients.flatMap((c) => c.stores.map((store) => ({ ...store, clientName: c.name })));
  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <StoreIcon className="size-4" /> Stores
        </CardTitle>
        <CardDescription>
          {org.status === "paused"
            ? "The org is paused — every store below is suspended regardless of its own status."
            : "Pause a single store to stop ingesting its data without touching the rest."}
        </CardDescription>
      </CardHeader>
      <CardContent>
        {stores.length === 0 ? (
          <p className="text-muted-foreground text-sm">No stores connected yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Store</TableHead>
                  <TableHead>Workspace</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stores.map((store) => (
                  <StoreRow key={store.id} store={store} client={client} onChanged={onChanged} />
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

function StoreRow({
  store,
  client,
  onChanged,
}: {
  readonly store: { id: string; shopDomain: string; displayName: string; status: string; clientName: string };
  readonly client: AxiosInstance;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const [busy, setBusy] = useState(false);
  const paused = store.status === "paused";
  const uninstalled = store.status === "uninstalled";

  async function toggle(): Promise<void> {
    setBusy(true);
    try {
      await setStoreStatus(client, store.id, paused ? "active" : "paused");
      onChanged();
    } catch (caught) {
      void caught;
      onChanged();
    } finally {
      setBusy(false);
    }
  }

  return (
    <TableRow>
      <TableCell>
        <div className="font-medium">{store.displayName}</div>
        <div className="text-muted-foreground text-xs">{store.shopDomain}</div>
      </TableCell>
      <TableCell className="text-muted-foreground">{store.clientName}</TableCell>
      <TableCell>
        {uninstalled ? (
          <Badge className="border-transparent bg-slate-500/15 text-slate-600">Uninstalled</Badge>
        ) : paused ? (
          <Badge className="border-transparent bg-amber-500/15 text-amber-700 dark:text-amber-400">Paused</Badge>
        ) : (
          <Badge variant="outline">Active</Badge>
        )}
      </TableCell>
      <TableCell className="text-right">
        <Button type="button" size="sm" variant="outline" disabled={busy || uninstalled} onClick={() => void toggle()}>
          {busy ? <Loader2 className="size-4 animate-spin" /> : null}
          {paused ? "Resume" : "Pause"}
        </Button>
      </TableCell>
    </TableRow>
  );
}

const STATUS_OPTIONS: readonly SubscriptionStatus[] = ["active", "trialing", "past_due", "paused", "canceled"];

function PlanCard({
  org,
  ent,
  plans,
  client,
  orgId,
  onChanged,
}: {
  readonly org: DirectoryOrg;
  readonly ent: EntitlementInspection;
  readonly plans: readonly PlanSummary[];
  readonly client: AxiosInstance;
  readonly orgId: string;
  readonly onChanged: () => void;
}): React.JSX.Element {
  const [planCode, setPlanCode] = useState<string>(ent.planCode ?? plans[0]?.code ?? "");
  const [status, setStatus] = useState<SubscriptionStatus>("active");
  const [busy, setBusy] = useState<null | "assign" | "comp">(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  async function apply(kind: "assign" | "comp", useStatus: SubscriptionStatus): Promise<void> {
    if (planCode === "") return;
    setBusy(kind);
    setError(null);
    setNotice(null);
    try {
      await assignPlan(client, orgId, planCode, useStatus);
      const planName = plans.find((p) => p.code === planCode)?.name ?? planCode;
      setNotice(
        kind === "comp"
          ? `${org.name} is now on ${planName}, free — no billing. It stays active until you change it.`
          : `${org.name} moved to ${planName} (${useStatus}).`,
      );
      onChanged();
    } catch (caught) {
      setError(apiErrorMessage(caught, "Could not update the subscription."));
    } finally {
      setBusy(null);
    }
  }

  const selectedFree = plans.find((p) => p.code === planCode)?.priceMinor === 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Plan &amp; billing</CardTitle>
        <CardDescription>
          {org.subscription === null ? (
            "No subscription yet."
          ) : (
            <span className="inline-flex items-center gap-1.5">
              On <span className="text-foreground font-medium">{org.subscription.planName}</span>
              <StatusBadge status={org.subscription.status} />
              via {org.subscription.provider}
              {isComped(org.subscription) && " (comped — no billing)"}
            </span>
          )}
        </CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="grid gap-1.5">
            <label htmlFor="plan" className="text-sm font-medium">
              Plan
            </label>
            <Select id="plan" value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
              {plans.map((p) => (
                <option key={p.code} value={p.code}>
                  {p.name} {p.priceMinor === 0 ? "(free)" : `(${(p.priceMinor / 100).toLocaleString()} ${p.currency})`}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid gap-1.5">
            <label htmlFor="status" className="text-sm font-medium">
              Status
            </label>
            <Select id="status" value={status} onChange={(e) => setStatus(e.target.value as SubscriptionStatus)}>
              {STATUS_OPTIONS.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </Select>
          </div>
        </div>

        {/*
          The comp action. It is just assignPlan with an active status — the server always
          records provider=manual, so nothing is ever charged — surfaced as its own labelled
          button so "give this org the platform free" is one deliberate click, not a status
          someone sets by accident.
        */}
        <div className="flex flex-col gap-2 bg-gray-50 rounded-lg border border-emerald-300  p-3 dark:border-emerald-900">
          <div className="flex items-center gap-2 text-sm font-medium text-black dark:text-black">
            <Gift className="size-4" /> Comp this organization
          </div>
          <p className="text-xs text-emerald-700 dark:text-gray-700">
            Puts <span className="font-medium">{org.name}</span> on the selected plan, active, with no billing — nothing
            is ever charged and no payment provider is involved. {selectedFree ? "" : "Tip: pick a free/internal plan for a clean audit trail."}
          </p>
          <Button
            type="button"
            onClick={() => void apply("comp", "active")}
            disabled={busy !== null || planCode === ""}
            className="w-fit bg-emerald-600 text-white hover:bg-emerald-500"
          >
            {busy === "comp" ? <Loader2 className="size-4 animate-spin" /> : <Gift className="size-4" />}
            Comp — free, no billing
          </Button>
        </div>

        {error !== null && (
          <p className="text-destructive text-sm" role="alert">
            {error}
          </p>
        )}
        {notice !== null && (
          <p className="text-sm text-emerald-700 dark:text-emerald-400" role="status">
            {notice}
          </p>
        )}

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => void apply("assign", status)}
            disabled={busy !== null || planCode === ""}
          >
            {busy === "assign" ? <Loader2 className="size-4 animate-spin" /> : null}
            Assign plan with status
          </Button>
          <span className="text-muted-foreground text-xs">Assigns exactly the status chosen above.</span>
        </div>
      </CardContent>
    </Card>
  );
}
